// Imports
import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Input, Switch } from "antd";
import WhatsappDirectFields from "../providerFields/WhatsappDirectFields";
import TwilioFields from "../providerFields/TwilioFields";
import FonadaFields from "../providerFields/FonadaFields";
import KaleyraFields from "../providerFields/KaleyraFields";
import CommonSelect from "../../../../../core/common/commonSelect";

import ConfirmationModal, {
  ConfirmationType,
} from "../../../../ers/common/components/ConfirmationModal";

// Types
export type ProviderType = "whatsapp_direct" | "twilio" | "fonada" | "kaleyra";

export interface WhatsappCredentialRecord {
  id: number;
  name: string;
  provider_type: ProviderType | string;
  phone_number: string;
  status: number;
  credentials: any;
}

type Mode = "add" | "edit";

interface WhatsappConfigModalProps {
  isOpen: boolean;
  mode: Mode;
  record?: WhatsappCredentialRecord | null;
  onClose: () => void;
  onSubmit: (payload: any) => Promise<void> | void;
  signature: string;
  companyId: number;
  companyType: string;
  defaultProvider?: ProviderType | string;
}

type FieldErrors = {
  name?: string;
  phoneNumber?: string;
  providerType?: string;
  credentials?: string;
};

// Constants
const providerOptions = [
  { value: "whatsapp_direct", label: "WhatsApp Direct" },
  { value: "twilio", label: "Twilio" },
  { value: "fonada", label: "Fonada" },
  { value: "kaleyra", label: "Kaleyra" },
];

const phoneRegex = /^\+?[1-9]\d{7,14}$/;

// Component
const WhatsappConfigModal: React.FC<WhatsappConfigModalProps> = ({
  isOpen,
  mode,
  record,
  onClose,
  onSubmit,
  signature,
  companyId,
  companyType,
  defaultProvider = "whatsapp_direct",
}) => {
  // State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [providerType, setProviderType] = useState<
    ProviderType | string | undefined
  >(defaultProvider);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [status, setStatus] = useState(true);
  const [credentials, setCredentials] = useState<any>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const modalRef = useRef<HTMLDivElement | null>(null);
  const mouseDownInside = useRef(false);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  // Effects
  useEffect(() => {
    if (!isOpen) return;

    setIsSubmitting(false);
    setErrors({});
    setIsConfirmOpen(false);
    setIsConfirmLoading(false);

    if (mode === "edit" && record) {
      setProviderType(record.provider_type);
      setName(record.name || "");
      setPhoneNumber(record.phone_number || "");
      setStatus(record.status === 1);
      setCredentials(record.credentials || {});
    } else {
      setProviderType(defaultProvider);
      setName("");
      setPhoneNumber("");
      setStatus(true);
      setCredentials({});
    }
  }, [isOpen, mode, record, defaultProvider]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isSubmitting && !isConfirmLoading) {
        if (isConfirmOpen) {
          setIsConfirmOpen(false);
        } else {
          onClose();
        }
      }
    };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, isSubmitting, isConfirmLoading, isConfirmOpen, onClose]);

  useEffect(() => {
    if (isOpen || isConfirmOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, isConfirmOpen]);

  // Helpers
  const handleChangeCredential = (key: string, value: string) => {
    setCredentials((prev: any) => ({ ...prev, [key]: value }));
    if (errors.credentials) {
      setErrors((p) => ({ ...p, credentials: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FieldErrors = {};

    if (!name.trim()) {
      newErrors.name = "Enter a configuration name.";
    }

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = "Enter a WhatsApp phone number.";
    } else if (!phoneRegex.test(phoneNumber.trim())) {
      newErrors.phoneNumber = "Use international format, e.g. +14155552671.";
    }

    if (!providerType) {
      newErrors.providerType = "Select a provider.";
    }

    if (providerType === "fonada") {
      const { api_key, api_url } = credentials;
      if (!api_key || !api_url) {
        newErrors.credentials = "Add Fonada API key and URL.";
      }
    }

    if (providerType === "twilio") {
      const { account_sid, auth_token, messaging_service_sid } = credentials;
      if (!account_sid || !auth_token || !messaging_service_sid) {
        newErrors.credentials =
          "Add Twilio Account SID, Auth Token and Messaging Service SID.";
      }
    }

    if (providerType === "whatsapp_direct") {
      const { access_token, phone_number_id, waba_id } = credentials;
      if (!access_token || !phone_number_id || !waba_id) {
        newErrors.credentials =
          "Add access token, phone number ID and WABA ID.";
      }
    }

    if (providerType === "kaleyra") {
      const { api_key, sid, api_domain } = credentials;
      if (!api_key || !sid || !api_domain) {
        newErrors.credentials = "Add Kaleyra API key, SID and API domain.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    signature,
    company_id: companyId,
    company_type: companyType,
    ...(mode === "edit" && record?.id ? { config_id: record.id } : {}),
    name,
    provider_type: providerType,
    phone_number: phoneNumber.trim(),
    status: status ? 1 : 0,
    credentials,
  });

  const getProviderSelectValue = () => {
    if (!providerType) return undefined;
    const current = providerOptions.find((p) => p.value === providerType);
    return current ? { value: current.value, label: current.label } : undefined;
  };

  // Handlers
  const handlePrimaryClick = () => {
    if (!validate()) return;
    setIsConfirmOpen(true);
  };

  const handleConfirmSubmit = useCallback(async () => {
    setIsConfirmLoading(true);
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      await onSubmit(payload);
      setIsConfirmOpen(false);
      onClose();
    } finally {
      setIsConfirmLoading(false);
      setIsSubmitting(false);
    }
  }, [
    onSubmit,
    onClose,
    mode,
    providerType,
    name,
    phoneNumber,
    status,
    credentials,
    record,
  ]);

  if (!isOpen) return null;

  const title =
    mode === "edit"
      ? "Edit WhatsApp Configuration"
      : "Add WhatsApp Configuration";
  const subtitle =
    mode === "edit"
      ? "Update the provider connection details for this company."
      : "Configure a new provider connection for this company.";
  const primaryLabel =
    mode === "edit" ? "Update Configuration" : "Save Configuration";

  const confirmTitle =
    mode === "edit" ? "Confirm Update" : "Confirm New Configuration";
  const confirmMessage =
    mode === "edit"
      ? "You are updating an existing WhatsApp configuration. If the credentials are invalid, message delivery and webhooks may break. Do you still want to continue?"
      : "Please review the configuration details carefully before saving. Incorrect credentials can prevent WhatsApp messages from being sent. Do you want to save this configuration?";
  const confirmType: ConfirmationType = mode === "edit" ? "warning" : "info";
  const confirmText = mode === "edit" ? "Yes, Update" : "Yes, Save";

  // Render
  return (
    <>
      {createPortal(
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: 24,
            boxSizing: "border-box",
          }}
          onMouseDown={(e) => {
            if (
              modalRef.current &&
              modalRef.current.contains(e.target as Node)
            ) {
              mouseDownInside.current = true;
            } else {
              mouseDownInside.current = false;
            }
          }}
          onMouseUp={(e) => {
            const clickedOutside =
              !mouseDownInside.current &&
              modalRef.current &&
              !modalRef.current.contains(e.target as Node);
            mouseDownInside.current = false;
            if (clickedOutside && !isSubmitting && !isConfirmLoading) onClose();
          }}>
          <div
            ref={modalRef}
            className="shadow-lg wa-config-modal"
            style={{
              background: "#ffffff",
              borderRadius: 18,
              width: "100%",
              maxWidth: 780,
              height: "92vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow:
                "0 28px 60px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(148,163,184,0.18)",
            }}>
            {/* Header */}
            <div
              className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom"
              style={{ background: "#f5f7fb" }}>
              <div>
                <h5 className="mb-0 fw-semibold">{title}</h5>
                <small className="text-muted">{subtitle}</small>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting || isConfirmLoading}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#6b7280",
                }}
                aria-label="Close">
                ×
              </button>
            </div>

            {/* Body */}
            <div
              style={{
                padding: "18px 24px 14px",
                overflowY: "auto",
                flex: 1,
              }}>
              <div
                className="mb-3"
                style={{
                  borderBottom: "1px solid #edf0f3",
                  paddingBottom: 8,
                }}>
                <span
                  className="text-muted"
                  style={{
                    fontSize: 11,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}>
                  Configuration Details
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                  columnGap: 20,
                  rowGap: 18,
                }}>
                {/* Name */}
                <div>
                  <label className="form-label fw-semibold mb-1">
                    Configuration Name <span className="text-danger">*</span>
                  </label>
                  <Input
                    placeholder="e.g. ATR Twilio WA - Production"
                    value={name}
                    status={errors.name ? "error" : undefined}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name)
                        setErrors((p) => ({ ...p, name: undefined }));
                    }}
                  />
                  {errors.name ? (
                    <small className="text-danger">{errors.name}</small>
                  ) : (
                    <small className="text-muted">
                      A readable label for this config.
                    </small>
                  )}
                </div>

                {/* Provider */}
                <div style={{ zIndex: 1 }}>
                  <label className="form-label fw-semibold mb-1">
                    Provider <span className="text-danger">*</span>
                  </label>
                  <CommonSelect
                    className="muted small w-100"
                    options={providerOptions}
                    defaultValue={getProviderSelectValue()}
                    placeHolder="Select provider"
                    onChange={(selected: any) => {
                      const selectedValue = selected?.value as
                        | ProviderType
                        | undefined;
                      setProviderType(selectedValue);
                      setCredentials({});
                      if (errors.providerType) {
                        setErrors((prev) => ({
                          ...prev,
                          providerType: undefined,
                        }));
                      }
                    }}
                  />
                  {errors.providerType ? (
                    <small className="text-danger">{errors.providerType}</small>
                  ) : (
                    <small className="text-muted">
                      Provider controls which credential fields appear below.
                    </small>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="form-label fw-semibold mb-1">
                    WhatsApp Phone Number <span className="text-danger">*</span>
                  </label>
                  <Input
                    placeholder="+14155255886"
                    value={phoneNumber}
                    status={errors.phoneNumber ? "error" : undefined}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      if (errors.phoneNumber)
                        setErrors((p) => ({
                          ...p,
                          phoneNumber: undefined,
                        }));
                    }}
                  />
                  {errors.phoneNumber ? (
                    <small className="text-danger">{errors.phoneNumber}</small>
                  ) : (
                    <small className="text-muted">
                      Example: +14155552671 (digits only, no spaces).
                    </small>
                  )}
                </div>

                {/* Status */}
                <div className="d-flex flex-column">
                  <label className="form-label fw-semibold mb-1">Status</label>
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: 10 }}>
                    <Switch
                      checked={status}
                      onChange={setStatus}
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                    />
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Inactive configs will not be used to send messages.
                    </span>
                  </div>
                </div>
              </div>

              {/* Provider-specific credentials */}
              <div
                className="mt-4 p-3 rounded"
                style={{
                  background: "#f9fafb",
                  border: "1px dashed #e5e7eb",
                }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold" style={{ fontSize: 13 }}>
                    {
                      providerOptions.find((p) => p.value === providerType)
                        ?.label
                    }{" "}
                    Credentials
                  </span>
                  <span
                    className="badge bg-light text-muted"
                    style={{ fontSize: 11 }}>
                    Sensitive · Encrypted
                  </span>
                </div>

                {errors.credentials && (
                  <div className="mb-2">
                    <small className="text-danger">{errors.credentials}</small>
                  </div>
                )}

                {providerType === "fonada" && (
                  <FonadaFields
                    credentials={credentials}
                    onChange={handleChangeCredential}
                  />
                )}

                {providerType === "twilio" && (
                  <TwilioFields
                    credentials={credentials}
                    onChange={handleChangeCredential}
                  />
                )}

                {providerType === "whatsapp_direct" && (
                  <WhatsappDirectFields
                    credentials={credentials}
                    onChange={handleChangeCredential}
                  />
                )}

                {providerType === "kaleyra" && (
                  <KaleyraFields
                    credentials={credentials}
                    onChange={handleChangeCredential}
                  />
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
              <div className="text-muted" style={{ fontSize: 11 }}>
                You can edit this configuration later from the list view.
              </div>
              <div className="d-flex" style={{ gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={onClose}
                  disabled={isSubmitting || isConfirmLoading}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePrimaryClick}
                  disabled={isSubmitting || isConfirmLoading}>
                  {isSubmitting || isConfirmLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving...
                    </>
                  ) : (
                    primaryLabel
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => !isConfirmLoading && setIsConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        title={confirmTitle}
        message={confirmMessage}
        confirmText={confirmText}
        cancelText="Go Back"
        type={confirmType}
        isLoading={isConfirmLoading}
        showCloseButton={false}
      />
    </>
  );
};

export default WhatsappConfigModal;
